import { useState, useEffect } from 'react';
import { handleApiError } from '../utils/apiHelpers';
import config from '../config/api.config'
import { logger } from '../utils/logger';


interface Branch {
  BranchID: number;
  BranchName: string;
}

interface BranchesHook {
  branches: Branch[];
  selectedBranch: number;
  branchName: string;
  loading: boolean;
  error: string | null;
  setSelectedBranch: (branchId: number) => void;
  // Data property for React Query compatibility
  data?: {
    BranchName: string;
    BranchID: number;
  };
}

export const useBranches = (defaultBranchId: number): BranchesHook => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number>(defaultBranchId);
  const [branchName, setBranchName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${config.API_URL}/brands/1`);
        if (!response.ok) throw new Error('Failed to fetch brands');
        
        const branchesResponse = await fetch(`${config.API_URL}/branches/brand/0`);
        if (!branchesResponse.ok) throw new Error('Failed to fetch branches');
        
        const branchesData = await branchesResponse.json();
        setBranches(branchesData);
      } catch (err) {
        logger.error('Error fetching branches', 'UseBranches', err);
        setError(handleApiError(err, 'بارگذاری شعب'));
      }
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchBranchInfo = async () => {
      setLoading(true);
      try {
        const branchResponse = await fetch(`${config.API_URL}/branches/${selectedBranch}`);
        if (!branchResponse.ok) throw new Error('Failed to fetch branch info');
        const branchData = await branchResponse.json();
        setBranchName(branchData.BranchName);
      } catch (err) {
        logger.error('Error fetching branch info', 'UseBranches', err);
        setError(handleApiError(err, 'بارگذاری اطلاعات شعبه'));
      } finally {
        setLoading(false);
      }
    };

    if (selectedBranch) {
      fetchBranchInfo();
    }
  }, [selectedBranch]);

  // Create a data property for React Query compatibility
  const data = branchName ? {
    BranchName: branchName,
    BranchID: selectedBranch
  } : undefined;

  return {
    branches,
    selectedBranch,
    branchName,
    loading,
    error,
    setSelectedBranch,
    data // Include data property for compatibility with React Query style
  };
};